import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { colorPalette } from 'shared/constants';
import { formatNumber, formatTitleCase } from 'shared/utils';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import { useMetricUserCases, useQualityMetricCase } from 'shared/hooks/quality-metric.hooks';
import {
  RadioSetFilter,
  Table,
  cellActionKeyboardNavigate,
  radioSetFilterDoesFilterPass,
} from 'src/components/design-system/table';
import { Button } from 'src/components/design-system';
import { Modal } from 'src/components/design-system/modal';
import { Loader } from 'src/components/core';
import { NotFound } from 'src/components/common';
import { CaseTeamColumn } from 'src/pages/application/quality-metrics/components/case-detail/case-team-column';
import { CaseInfoColumn } from 'src/pages/application/quality-metrics/components/case-detail/case-info-column';
import { CaseComplianceColumn } from 'src/pages/application/quality-metrics/components/case-detail/case-compliance-column';
import { useQualityMetricsManagementContext } from '../quality-metrics-management.context';
import styles from '../quality-metrics-management.module.scss';

// `autoHeight` disables row virtualisation, so the grid's own pagination is
// what keeps the DOM to one page however many cases the user has.
const GRID_PAGE_SIZE = 20;
const GRID_PAGE_SIZE_OPTIONS = [20, 50, 100];

function ComplianceCell({ value, data }) {
  const color = data?.compliant ? colorPalette.greenPositive : colorPalette.redNegative;
  return (
    <span className={styles.complianceCell} style={{ color }}>
      <FontAwesomeIcon icon={data?.compliant ? faCheckCircle : faTriangleExclamation} />
      {value}
    </span>
  );
}

/** The case detail, rendered from the same columns the clinician case page uses. */
function CaseDetailView({ caseId, onBack, lang, metricLang }) {
  const { data: caseData, isLoading, error } = useQualityMetricCase(caseId);

  const title = caseData
    ? formatTitleCase(caseData.metadata?.procedure) || caseData.externalId
    : '';

  return (
    <>
      <Button type="link" className={styles.backButton} onClick={onBack}>
        <FontAwesomeIcon icon={faChevronLeft} /> {lang.BACK_TO_CASES}
      </Button>

      {isLoading && <Loader />}
      {!isLoading && (error || !caseData) && <NotFound text={metricLang.NO_DATA} />}

      {!isLoading && caseData && (
        <>
          <h3 className={styles.caseTitle}>{title}</h3>
          <div className={styles.caseColumns}>
            <CaseTeamColumn participants={caseData.participants} lang={metricLang} />
            <CaseInfoColumn caseData={caseData} lang={metricLang} />
            <CaseComplianceColumn metrics={caseData.metrics} lang={metricLang} />
          </div>
        </>
      )}
    </>
  );
}

function CasesListView({ userId, onOpenCase, lang, metricLang }) {
  const { selectedMetric } = useQualityMetricsManagementContext();
  const { formatLongDateTime } = useDateFormatter();
  // Every case, in one list: the grid filters, sorts and paginates the whole
  // set, so its row count is the same number the summary above it reports.
  const { data, isLoading } = useMetricUserCases(selectedMetric?.id, userId);

  const complianceFilterOptions = useMemo(
    () => [
      {
        value: metricLang.COMPLIANT,
        label: metricLang.COMPLIANT,
        matcher: v => v === metricLang.COMPLIANT,
      },
      {
        value: metricLang.NON_COMPLIANT,
        label: metricLang.NON_COMPLIANT,
        matcher: v => v === metricLang.NON_COMPLIANT,
      },
    ],
    [metricLang],
  );

  const columns = useMemo(
    /** @type {() => import('ag-grid-community').ColDef[]} */
    () => [
      {
        field: 'title',
        headerName: metricLang.CASE_PROCEDURE,
        flex: 2,
        filter: true,
        // `title` is empty when the record carried no `metadata.procedure`;
        // the case id is the only other thing that identifies the row.
        valueGetter: ({ data: row }) => formatTitleCase(row.title) || row.id,
      },
      {
        field: 'date',
        headerName: metricLang.CASE_DATE,
        flex: 1,
        valueFormatter: params => (params.value ? formatLongDateTime(params.value) : ''),
      },
      {
        field: 'compliant',
        headerName: metricLang.COMPLIANCE_HEADER,
        flex: 1,
        cellRenderer: ComplianceCell,
        valueGetter: ({ data: row }) =>
          row.compliant ? metricLang.COMPLIANT : metricLang.NON_COMPLIANT,
        filter: { component: RadioSetFilter, doesFilterPass: radioSetFilterDoesFilterPass },
        filterParams: { options: complianceFilterOptions },
      },
      {
        field: 'reason',
        headerName: lang.CASE_REASON,
        flex: 2,
        valueGetter: ({ data: row }) => row.reason || '',
      },
      {
        headerName: lang.COLUMN_ACTIONS,
        colId: 'actions',
        flex: 1,
        sortable: false,
        // A focusable control, not just `onRowClicked`: row clicks are
        // mouse-only, so without this a keyboard user cannot open a case.
        cellRenderer: ({ data: row }) => (
          <Button
            type="link"
            aria-label={lang.OPEN_CASE_LABEL.replace(
              '{case}',
              formatTitleCase(row.title) || row.id,
            )}
            onClick={event => {
              // The row handler would fire too; one open is enough.
              event.stopPropagation();
              onOpenCase(row.id);
            }}>
            {lang.OPEN_CASE}
          </Button>
        ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
    ],
    [metricLang, lang, formatLongDateTime, complianceFilterOptions, onOpenCase],
  );

  if (isLoading) {
    return <Loader />;
  }

  const cases = data?.cases ?? [];

  if (!cases.length) {
    return <p className={styles.emptyText}>{metricLang.NO_CASES}</p>;
  }

  return (
    <>
      <p className={styles.muted}>
        {lang.CASES_SUMMARY.replace('{total}', formatNumber(data?.total ?? 0)).replace(
          '{compliant}',
          formatNumber(data?.totalCompliant ?? 0),
        )}
      </p>

      <Table
        containerProps={{ className: styles.usersTable }}
        columnDefs={columns}
        rowData={cases}
        getRowId={params => params.data.id}
        domLayout="autoHeight"
        pagination
        paginationPageSize={GRID_PAGE_SIZE}
        paginationPageSizeSelector={GRID_PAGE_SIZE_OPTIONS}
        enableActiveFilters
        onRowClicked={event => onOpenCase(event.data.id)}
      />
    </>
  );
}

/**
 * A user's cases on the selected metric, and the detail of any one of them.
 *
 * The list comes from the Root-only `GET /:id/users/:userId/cases`; the detail
 * is the same `GET /cases/:caseId` the clinician case page uses, rendered
 * through that page's own column components so the two cannot drift.
 */
export function UserCasesModal({ open, user, onClose }) {
  const { lang, metricLang } = useQualityMetricsManagementContext();
  const [caseId, setCaseId] = useState(null);

  const userName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email
    : '';

  const handleClose = () => {
    setCaseId(null);
    onClose();
  };

  return (
    <Modal
      title={lang.USER_CASES_TITLE.replace('{name}', userName)}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={1100}
      className={styles.casesModal}>
      {caseId ? (
        <CaseDetailView
          caseId={caseId}
          onBack={() => setCaseId(null)}
          lang={lang}
          metricLang={metricLang}
        />
      ) : (
        user && (
          <CasesListView
            userId={user.userId}
            onOpenCase={setCaseId}
            lang={lang}
            metricLang={metricLang}
          />
        )
      )}
    </Modal>
  );
}
